import React, { useState } from 'react';
import { BIO } from '@/constants/testIds';

/**
 * ProfileCard — centralized identity card.
 *
 * Renders a circular avatar:
 *   - If `photoUrl` is provided AND loads successfully, show the photo.
 *   - Otherwise, show the dashed-border initials placeholder (the original
 *     wireframe state). This guarantees the layout never breaks if a hosted
 *     asset 404s or is blocked inside a Shopify iframe.
 */
const ProfileCard = ({ name, title, bio, photoUrl }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showPhoto = Boolean(photoUrl) && !imageFailed;

  // Initials placeholder (derived from name, not a network image).
  const initials = (name || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <section
      className="lib-card lib-profile-card"
      data-testid={BIO.profileCard}
      aria-label="Profile"
    >
      <div
        className={`lib-avatar ${showPhoto ? 'lib-avatar--photo' : ''}`}
        data-testid={BIO.profileAvatar}
        aria-hidden="true"
      >
        {showPhoto ? (
          <img
            className="lib-avatar-image"
            src={photoUrl}
            alt={`${name} profile`}
            onError={() => setImageFailed(true)}
            loading="eager"
            decoding="async"
          />
        ) : (
          <>
            <span className="lib-avatar-initials">{initials || 'BW'}</span>
            <span className="lib-avatar-placeholder-label">Profile Image</span>
          </>
        )}
      </div>

      <h1 className="lib-profile-name" data-testid={BIO.profileName}>
        {name}
      </h1>
      <p className="lib-profile-title" data-testid={BIO.profileTitle}>
        {title}
      </p>
      <p className="lib-profile-bio" data-testid={BIO.profileBio}>
        {bio}
      </p>
    </section>
  );
};

export default ProfileCard;
